import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./style.css";

// Защита: возвращает массив в любом случае
const toArray = (val, fallback = []) => (Array.isArray(val) ? val : fallback);

// ─── LivePreview ─────────────────────────────────────────────────────────────
function LivePreview() {
  const { t } = useTranslation("hero");
  const services = toArray(t("services", { returnObjects: true }), [
    { title: "Websites", slug: "websites", text: "", accent: "#ff7a3c" },
  ]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (services.length < 2) return;
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setActiveIndex((prev) => (prev + 1) % services.length);
        setIsAnimating(false);
      }, 260);
    }, 4000);
    return () => clearInterval(interval);
  }, [services.length]);

  const safeIndex = activeIndex % services.length;
  const active = services[safeIndex];

  const handleDotClick = (i) => {
    if (i === safeIndex) return;
    setIsAnimating(true);
    setTimeout(() => {
      setActiveIndex(i);
      setIsAnimating(false);
    }, 260);
  };

  const metrics = toArray(t("livePreview.metrics", { returnObjects: true }));

  return (
    <div className="hp-live">
      <div className="hp-live__header">
        <span className="hp-live__badge">{t("livePreview.badge")}</span>

        <div className="hp-live__indicators">
          {services.map((service, i) => (
            <button
              key={service.title}
              type="button"
              className={`hp-live__dot ${i === safeIndex ? "hp-live__dot--active" : ""}`}
              onClick={() => handleDotClick(i)}
              style={{
                backgroundColor:
                  i === safeIndex ? service.accent : "rgba(255,255,255,0.2)",
              }}
              aria-label={`${t("livePreview.showAriaLabel")} ${service.title}`}
            />
          ))}
        </div>
      </div>

      <div
        className={`hp-live__preview ${isAnimating ? "hp-live__preview--exiting" : ""}`}
        style={{ "--accent": active.accent }}
      >
        <div className="hp-live__mockup">
          <div className="hp-live__mockup-header">
            <div className="hp-live__mockup-dots">
              <span style={{ backgroundColor: "#ff5f56" }} />
              <span style={{ backgroundColor: "#ffbd2e" }} />
              <span style={{ backgroundColor: "#27c93f" }} />
            </div>
            <div className="hp-live__mockup-url">
              pixelcraft/{active.slug}
            </div>
          </div>

          <div className="hp-live__mockup-content">
            <div
              className="hp-live__mockup-title"
              style={{ color: active.accent }}
            >
              {active.title}
            </div>
            <div className="hp-live__mockup-text">{active.text}</div>

            <div className="hp-live__mockup-progress">
              <div className="hp-live__mockup-bar">
                <div
                  className="hp-live__mockup-fill"
                  style={{
                    width: `${((safeIndex + 1) / services.length) * 100}%`,
                    backgroundColor: active.accent,
                  }}
                />
              </div>
              <span className="hp-live__mockup-stats">
                {safeIndex + 1}/{services.length}{" "}
                {t("livePreview.focusAreas")}
              </span>
            </div>
          </div>
        </div>

        <div className="hp-live__metrics">
          {metrics.map((m) => (
            <div key={m.label} className="hp-live__metric">
              <span
                className="hp-live__metric-value"
                style={{ color: active.accent }}
              >
                {m.value}
              </span>
              <span className="hp-live__metric-label">{m.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── ProcessCard ──────────────────────────────────────────────────────────────
function ProcessCard() {
  const { t } = useTranslation("hero");
  const steps = toArray(t("process.steps", { returnObjects: true }));

  return (
    <div className="hp-process">
      <div className="hp-process__label">{t("process.label")}</div>

      <div className="hp-process__list">
        {steps.map((item) => (
          <div key={item.number} className="hp-process__item">
            <div className="hp-process__top">
              <span className="hp-process__number">{item.number}</span>
              <span className="hp-process__title">{item.title}</span>
            </div>
            <p className="hp-process__text">{item.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function HeroSection() {
  const { t } = useTranslation("hero");

  const typingWords = toArray(t("typingWords", { returnObjects: true }), [
    "Designer",
  ]);
  const stack = toArray(t("stack", { returnObjects: true }));

  const [typed, setTyped] = useState("");
  const strIdx = useRef(0);
  const charIdx = useRef(0);
  const deleting = useRef(false);
  const wordsRef = useRef(typingWords);

  // Обновляем ref при смене языка
  useEffect(() => {
    wordsRef.current = typingWords;
  }, [typingWords]);

  // Сброс анимации при смене языка
  const wordsKey = typingWords.join(",");
  useEffect(() => {
    strIdx.current = 0;
    charIdx.current = 0;
    deleting.current = false;
    setTyped("");
  }, [wordsKey]);

  // Основной typed-эффект
  useEffect(() => {
    let timeoutId;

    const tick = () => {
      const words = wordsRef.current;
      if (!words.length) return;

      const current = words[strIdx.current % words.length];

      if (!deleting.current) {
        charIdx.current += 1;
        setTyped(current.slice(0, charIdx.current));

        if (charIdx.current === current.length) {
          deleting.current = true;
          timeoutId = setTimeout(tick, 1800);
          return;
        }
      } else {
        charIdx.current -= 1;
        setTyped(current.slice(0, charIdx.current));

        if (charIdx.current === 0) {
          deleting.current = false;
          strIdx.current = (strIdx.current + 1) % words.length;
        }
      }

      timeoutId = setTimeout(tick, deleting.current ? 60 : 100);
    };

    timeoutId = setTimeout(tick, 600);
    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <div className="hero-proposal">
      <section className="hp-hero">
        {/* ── LEFT ── */}
        <div className="hp-hero__left">
          <div className="hp-hero__status">
            <div className="hp-hero__status-dot" />
            <span className="hp-hero__status-text">{t("status")}</span>
          </div>

          <div className="hp-hero__eyebrow">{t("eyebrow")}</div>

          <h1 className="hp-hero__title">
            {t("titlePrefix")} <em>{t("titleName")}</em>
          </h1>

          <div className="hp-hero__typed-row">
            <span className="hp-hero__typed">{typed}</span>
            <span className="hp-hero__cursor" />
          </div>

          <p className="hp-hero__text">{t("text")}</p>

          <div className="hp-hero__cta">
            <a href="/contacts" className="hp-btn button">
              {t("ctaButton")}
            </a>
          </div>
        </div>

        {/* ── RIGHT ── */}
        <div className="hp-hero__right">
          <LivePreview />
          <ProcessCard />

          <div className="hp-stack">
            {stack.map((item) => (
              <span key={item} className="hp-stack__chip">
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}